import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../../lib/api';
import { useT, type TranslationKey } from '../../i18n';
import { formatAge } from '../../lib/format';
import styles from './NotificationBell.module.css';

export interface AppNotification {
  id: string;
  type: 'TKP_ATTACHED' | 'MANAGER_ASSIGNED' | 'STAGE_ADVANCED' | string;
  inquiryId: string | null;
  message: string;
  read: boolean;
  createdAt: string;
}

const POLL_INTERVAL_MS = 45_000;

/** Server type → dictionary key (keeps t() statically typed). */
const TYPE_KEYS: Record<string, TranslationKey> = {
  TKP_ATTACHED: 'notifications.type.tkpAttached',
  MANAGER_ASSIGNED: 'notifications.type.managerAssigned',
  STAGE_ADVANCED: 'notifications.type.stageAdvanced',
};

/**
 * Issue #32 — in-app notifications. Bell in the top bar with an unread
 * badge and a dropdown. Plain fetch polling every ~45s (no websockets on
 * the box); clicking an item marks it read and opens the inquiry drawer.
 */
export function NotificationBell() {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const list = await api.get<AppNotification[]>('/notifications', { signal });
      setNotifications(list);
    } catch {
      // Poll failures are silent — the next tick retries.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    const id = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      window.clearInterval(id);
    };
  }, [refresh]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClickAway = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markRead = async (n: AppNotification) => {
    if (n.read) return;
    // Optimistic — the bell badge should react instantly
    setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch {
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: false } : x)));
    }
  };

  const markAllRead = async () => {
    const prev = notifications;
    setNotifications(prev.map((x) => ({ ...x, read: true })));
    try {
      await api.post('/notifications/read-all');
    } catch {
      setNotifications(prev);
    }
  };

  const handleItemClick = (n: AppNotification) => {
    void markRead(n);
    setOpen(false);
    if (n.inquiryId) navigate(`/inquiries?open=${n.inquiryId}`);
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.bell}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('notifications.bell')}
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className={styles.badge} aria-label={t('notifications.unread', { count: unreadCount })}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className={styles.dropdown} role="menu" aria-label={t('notifications.title')}>
          <div className={styles.header}>
            <span className={styles.title}>{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <button type="button" className={styles.markAll} onClick={() => void markAllRead()}>
                <CheckCheck size={13} aria-hidden="true" />
                <span>{t('notifications.markAllRead')}</span>
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className={styles.empty}>{t('notifications.empty')}</div>
          ) : (
            <ul className={styles.list}>
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    role="menuitem"
                    className={clsx(styles.item, !n.read && styles.itemUnread)}
                    onClick={() => handleItemClick(n)}
                  >
                    <span className={styles.itemTop}>
                      <span className={styles.itemType}>{t(TYPE_KEYS[n.type] ?? 'notifications.title')}</span>
                      <span className={styles.itemAge}>{formatAge(n.createdAt, locale)}</span>
                    </span>
                    <span className={styles.itemMessage}>{n.message}</span>
                    {!n.read && (
                      <span className={styles.unreadMark} aria-label={t('notifications.unreadOne')}>
                        <Check size={12} aria-hidden="true" />
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
